<template>
    <template v-if="pendingTerm">
        <group-terms-and-conditions-owner-modal
            v-if="canAccept"
            v-model:visible="ownerModal"
            :pendingTerm="pendingTerm"
            :hasTakenEffect="hasTakenEffect"
            :formattedEffectiveAt="formattedEffectiveAt"
            @accepted="pendingTerm = null"
        ></group-terms-and-conditions-owner-modal>
        <group-terms-and-conditions-user-modal
            v-else
            v-model:visible="nonOwnerModal"
            :owner="owner"
            :orgUnit="orgUnit"
            :pendingTerm="pendingTerm"
            :hasTakenEffect="hasTakenEffect"
            :formattedEffectiveAt="formattedEffectiveAt"
        ></group-terms-and-conditions-user-modal>
    </template>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import termsClient from '~/services/termsClient';
import type { TermSignerContact, OrgUnitSummary, Term, TermCoverage } from '~/services/termsClient.types';
import { shortDate } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import GroupTermsAndConditionsOwnerModal from './GroupTermsAndConditionsOwnerModal.vue';
import GroupTermsAndConditionsUserModal from './GroupTermsAndConditionsUserModal.vue';

const teamId = Spark.state.currentTeam?.id ?? null;
const groupId = Spark.state.currentGroup?.group_id ?? null;
const userId = Spark.state.user?.id ?? null;
const isOwner = Spark.roleOnCurrentGroup === 'owner';

const storageKey = `voxie-group-tnc.seenModalAt.${userId}.${teamId}.${groupId}`;

const ownerModal = ref(true);
const nonOwnerModal = ref(true);

const pendingTerm = ref<Term | null>(null);
const owner = ref<TermSignerContact | undefined>(undefined);
const coverage = ref<TermCoverage | null>(null);
const orgUnit = ref<OrgUnitSummary | null>(null);

const canAccept = computed(() => isOwner && (coverage.value ?? 'group') === 'group');

const formattedEffectiveAt = computed(() => {
    if (!pendingTerm.value) {
        return '';
    }
    return shortDate(pendingTerm.value.effective_at);
});

const hasTakenEffect = computed(() =>
    pendingTerm.value ? dayjs.utc(pendingTerm.value.effective_at).isBefore(dayjs()) : false
);

// pre-effective, the modal is only shown once every 2 hours; once the stored
// effective date has passed we must re-check so the modal can start blocking
const seenRecently = () => {
    const raw = localStorage.getItem(storageKey);

    if (!raw) {
        return false;
    }

    try {
        const seen: { seenAt: number; effectiveAt: string } = JSON.parse(raw);

        if (dayjs.utc(seen.effectiveAt).isBefore(dayjs())) {
            return false;
        }

        return dayjs(seen.seenAt).add(2, 'hours').isAfter(dayjs());
    } catch {
        return false;
    }
};

const setSeenModalAt = () => {
    if (hasTakenEffect.value) {
        return;
    }

    if (!pendingTerm.value) {
        return;
    }

    localStorage.setItem(
        storageKey,
        JSON.stringify({ seenAt: Date.now(), effectiveAt: pendingTerm.value.effective_at })
    );
};

const loadActive = async () => {
    if (!teamId || !groupId || seenRecently()) {
        return;
    }

    try {
        const { data } = await termsClient.getActiveTerm(teamId, groupId);
        const term = data.accepted ? null : data.term;
        owner.value = data.owner || undefined;
        coverage.value = data.coverage;
        orgUnit.value = data.org_unit;

        if (!term) {
            return;
        }

        pendingTerm.value = term;
    } catch (error) {
        console.error('Failed to load active T&C', error);
        pendingTerm.value = null;
        owner.value = undefined;
        coverage.value = null;
        orgUnit.value = null;
    }
};

watch(ownerModal, (isVisible) => {
    if (!isVisible) {
        setSeenModalAt();
    }
});

watch(nonOwnerModal, (isVisible) => {
    if (!isVisible) {
        setSeenModalAt();
    }
});

onMounted(() => {
    if (!Spark.isImpersonating) {
        loadActive();
    }
});
</script>
